import { Helmet } from "react-helmet-async";
import { useLang } from "../context/LangContext";
import { ArrowRight, CheckCircle, Truck, RefreshCw, Shield, Route, FileText, MessageCircle, Phone, Star, Clock, Users, Calendar, HeadphonesIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

const serviceData: Record<string, {
  slug: string; title: string; titleAr: string;
  desc: string; descAr: string;
  longDesc: string; longDescAr: string;
  metaTitle: string; metaTitleAr: string;
  metaDesc: string; metaDescAr: string;
  icon: React.ReactNode;
  features: string[]; featuresAr: string[];
  benefits: string[]; benefitsAr: string[];
}> = {
  "road-transportation": {
    slug: "road-transportation",
    title: "Road Transportation",
    titleAr: "النقل البري",
    desc: "Reliable road transportation across Bahrain and GCC countries",
    descAr: "نقل بري موثوق عبر البحرين ودول الخليج",
    metaTitle: "Road Transportation Bahrain | GCC Road Freight Services | ABU ALI LOGISTICS",
    metaTitleAr: "النقل البري في البحرين | خدمات الشحن البري لدول الخليج | أبو علي اللوجستية",
    metaDesc: "Professional road transportation services across Bahrain, Saudi Arabia, UAE, Kuwait, Qatar, Oman, Yemen, Syria & Jordan. Reliable road freight at competitive rates.",
    metaDescAr: "خدمات النقل البري الاحترافية عبر البحرين والسعودية والإمارات والكويت وقطر وعمان واليمن وسوريا والأردن. شحن بري موثوق بأسعار تنافسية.",
    longDesc: "We provide comprehensive road transportation services across the GCC, Yemen, Syria & Jordan, connecting Bahrain to Saudi Arabia, UAE, Kuwait, Qatar, Oman, Yemen, Syria, and Jordan. Our fleet handles commercial cargo of various sizes with a focus on safety, reliability, and on-time delivery at competitive freight rates. Whether you need regular scheduled runs or urgent same-day service, our experienced drivers and logistics coordinators ensure your goods reach their destination securely.",
    longDescAr: "نحن نقدم خدمات نقل بري شاملة عبر دول الخليج واليمن وسوريا والأردن، لربط البحرين بالسعودية والإمارات والكويت وقطر وعمان واليمن وسوريا والأردن. أسطولنا يتعامل مع البضائع التجارية بمختلف الأحجام مع التركيز على السلامة والموثوقية والتسليم في الوقت المحدد بأسعار شحن تنافسية. سواء كنت بحاجة إلى رحلات مجدولة منتظمة أو خدمة عاجلة في نفس اليوم، يضمن سائقونا ومنسقو اللوجستيات ذوو الخبرة وصول بضائعك بأمان.",
    icon: <Truck className="w-8 h-8" />,
    features: ["Bahrain to GCC Routes", "Timely Delivery", "Secure Transport", "Fleet Management"],
    featuresAr: ["طرق البحرين إلى الخليج", "توصيل في الوقت المحدد", "نقل آمن", "إدارة الأسطول"],
    benefits: ["Competitive freight rates across all GCC corridors", "Real-time shipment tracking", "Flexible scheduling including express options", "Dedicated account management"],
    benefitsAr: ["أسعار شحن تنافسية عبر جميع ممرات الخليج", "تتبع الشحنات في الوقت الفعلي", "جدولة مرنة تشمل خيارات سريعة", "إدارة حسابات مخصصة"],
  },
  "re-export": {
    slug: "re-export",
    title: "Re-Export Services",
    titleAr: "خدمات إعادة التصدير",
    desc: "Professional re-export solutions from Bahrain",
    descAr: "حلول إعادة تصدير احترافية من البحرين",
    metaTitle: "Re-Export Services Bahrain | Re-Export from Bahrain | ABU ALI LOGISTICS",
    metaTitleAr: "خدمات إعادة التصدير في البحرين | إعادة تصدير من البحرين | أبو علي اللوجستية",
    metaDesc: "Complete re-export documentation and logistics support from Bahrain. Fast processing, customs compliance, and door-to-door re-export solutions across the GCC.",
    metaDescAr: "توثيق ودعم لوجستي كامل لإعادة التصدير من البحرين. معالجة سريعة، امتثال جمركي، وحلول إعادة تصدير من الباب إلى الباب عبر الخليج.",
    longDesc: "Bahrain's strategic location and free trade agreements make it an ideal hub for re-export operations. We handle the complete re-export process including documentation, customs clearance, and transportation. Our team ensures compliance with all GCC re-export regulations, manages the paperwork, and coordinates the movement of goods from Bahrain to their final destination in any GCC country.",
    longDescAr: "موقع البحرين الاستراتيجي واتفاقيات التجارة الحرة يجعلانها مركزاً مثالياً لعمليات إعادة التصدير. نحن نتولى عملية إعادة التصدير الكاملة بما في ذلك التوثيق والتخليص الجمركي والنقل. يضمن فريقنا الامتثال لجميع لوائح إعادة التصدير في دول الخليج، ويدير الأوراق، وينسق حركة البضائع من البحرين إلى وجهتها النهائية في أي دولة خليجية.",
    icon: <RefreshCw className="w-8 h-8" />,
    features: ["Re-Export Documentation", "Customs Support", "Regulatory Compliance", "Fast Processing"],
    featuresAr: ["وثائق إعادة التصدير", "دعم جمركي", "الامتثال التنظيمي", "معالجة سريعة"],
    benefits: ["Leverage Bahrain's free trade zone benefits", "End-to-end documentation management", "Fast customs processing for re-exports", "Cost-effective logistics solutions"],
    benefitsAr: ["استفد من مزايا المنطقة الحرة في البحرين", "إدارة شاملة للوثائق", "معالجة جمركية سريعة لإعادة التصدير", "حلول لوجستية فعالة من حيث التكلفة"],
  },
  "customs-clearance": {
    slug: "customs-clearance",
    title: "Customs Clearance",
    titleAr: "التخليص الجمركي",
    desc: "Fast customs clearance for imports, exports and re-exports",
    descAr: "تخليص جمركي سريع للشحنات المستوردة والمصدرة والمعاد تصديرها",
    metaTitle: "Customs Clearance Bahrain | Import Export Clearance GCC | ABU ALI LOGISTICS",
    metaTitleAr: "التخليص الجمركي في البحرين | تخليص استيراد وتصدير الخليج | أبو علي اللوجستية",
    metaDesc: "Fast and reliable customs clearance services for imports, exports, and re-exports across all GCC borders. Complete document preparation and customs coordination.",
    metaDescAr: "خدمات تخليص جمركي سريعة وموثوقة للواردات والصادرات وإعادة التصدير عبر جميع حدود دول الخليج. إعداد كامل للمستندات وتنسيق جمركي.",
    longDesc: "Navigating GCC customs regulations requires expertise and experience. Our customs clearance team handles everything from document preparation to inspection coordination and duty payment. We clear shipments through all GCC border points including King Fahd Causeway, Al Batha, and other key crossings. Our deep understanding of GCC customs procedures ensures minimal delays and full compliance.",
    longDescAr: "يتطلب التنقل في لوائح الجمارك الخليجية خبرة وتجربة. فريق التخليص الجمركي لدينا يتولى كل شيء من إعداد المستندات إلى تنسيق التفتيش ودفع الرسوم. نقوم بتخليص الشحنات عبر جميع نقاط الحدود الخليجية بما في ذلك جسر الملك فهد والبدع والمعابر الرئيسية الأخرى. فهمنا العميق لإجراءات الجمارك الخليجية يضمن الحد الأدنى من التأخير والامتثال الكامل.",
    icon: <Shield className="w-8 h-8" />,
    features: ["Import Clearance", "Export Clearance", "Re-Export Clearance", "Document Preparation"],
    featuresAr: ["تخليص استيراد", "تخليص تصدير", "تخليص إعادة تصدير", "إعداد المستندات"],
    benefits: ["Expert handling of all GCC customs procedures", "Electronic customs submission", "Pre-clearance and advance ruling support", "Duty and tax calculation assistance"],
    benefitsAr: ["معالجة خبيرة لجميع الإجراءات الجمركية الخليجية", "تقديم جمركي إلكتروني", "دعم التخليص المسبق والأحكام المتقدمة", "مساعدة في حساب الرسوم والضرائب"],
  },
  "cross-border": {
    slug: "cross-border",
    title: "Cross Border Shipping",
    titleAr: "الشحن عبر الحدود",
    desc: "Efficient cross-border cargo transportation between GCC countries",
    descAr: "شحن بضائع فعال عبر الحدود بين دول الخليج",
    metaTitle: "Cross Border Shipping GCC | GCC Cargo Transport | ABU ALI LOGISTICS",
    metaTitleAr: "الشحن عبر حدود الخليج | نقل البضائع بين دول الخليج | أبو علي اللوجستية",
    metaDesc: "Efficient cross-border cargo transportation between all GCC countries. Full customs compliance, route planning, and border coordination.",
    metaDescAr: "شحن بضائع فعال عبر الحدود بين جميع دول الخليج. امتثال جمركي كامل، تخطيط الطرق، وتنسيق حدودي.",
    longDesc: "Cross-border shipping between GCC countries requires meticulous coordination of customs procedures, documentation, and transport logistics. We manage every aspect of cross-border movement, from route planning to border crossing coordination. Our drivers are experienced in all GCC border procedures, ensuring smooth passage through customs checkpoints. We handle both northbound and southbound routes across the GCC.",
    longDescAr: "يتطلب الشحن عبر الحدود بين دول الخليج تنسيقاً دقيقاً للإجراءات الجمركية والوثائق واللوجستيات النقلية. نحن ندير كل جانب من جوانب النقل عبر الحدود، من تخطيط الطرق إلى تنسيق عبور الحدود. سائقونا ذوو خبرة في جميع إجراءات الحدود الخليجية، مما يضمن مروراً سلساً عبر نقاط التفتيش الجمركية. نتعامل مع الطرق المتجهة شمالاً وجنوباً عبر دول الخليج.",
    icon: <Route className="w-8 h-8" />,
    features: ["GCC Cross Border", "Customs Documentation", "Route Planning", "Border Coordination"],
    featuresAr: ["شحن عبر حدود الخليج", "توثيق جمركي", "تخطيط الطرق", "تنسيق حدودي"],
    benefits: ["Seamless GCC border crossing procedures", "Optimized route planning for faster delivery", "Coordination with all GCC customs authorities", "Real-time border status updates"],
    benefitsAr: ["إجراءات عبور حدود خليجية سلسة", "تخطيط طرق محسن لتسليم أسرع", "تنسيق مع جميع السلطات الجمركية الخليجية", "تحديثات فورية لحالة الحدود"],
  },
  "import-export-documentation": {
    slug: "import-export-documentation",
    title: "Import & Export Documentation",
    titleAr: "مستندات الاستيراد والتصدير",
    desc: "Preparation and management of all required shipping documentation",
    descAr: "إعداد وإدارة جميع مستندات الشحن المطلوبة",
    metaTitle: "Import Export Documentation Bahrain | Shipping Documents GCC | ABU ALI LOGISTICS",
    metaTitleAr: "مستندات الاستيراد والتصدير البحرين | وثائق الشحن الخليج | أبو علي اللوجستية",
    metaDesc: "Complete import and export documentation services for GCC trade. Commercial invoices, certificates of origin, bills of lading, and customs forms.",
    metaDescAr: "خدمات توثيق كاملة للاستيراد والتصدير للتجارة الخليجية. الفواتير التجارية، شهادات المنشأ، سندات الشحن، والنماذج الجمركية.",
    longDesc: "Proper documentation is critical for smooth international trade. We prepare and manage all required shipping documentation including commercial invoices, packing lists, certificates of origin, bills of lading, and customs declarations. Our documentation specialists ensure every form is accurate and compliant with both origin and destination country requirements, preventing costly delays and customs rejections.",
    longDescAr: "التوثيق السليم أمر بالغ الأهمية للتجارة الدولية السلسة. نقوم بإعداد وإدارة جميع مستندات الشحن المطلوبة بما في ذلك الفواتير التجارية وقوائم التعبئة وشهادات المنشأ وسندات الشحن والتصريحات الجمركية. يضمن متخصصو التوثيق لدينا أن كل نموذج دقيق ومتوافق مع متطلبات بلد المنشأ والوجهة، مما يمنع التأخير المكلف ورفض الجمارك.",
    icon: <FileText className="w-8 h-8" />,
    features: ["Document Preparation", "Compliance Checks", "Regulatory Filing", "Digital Records"],
    featuresAr: ["إعداد المستندات", "فحوص الامتثال", "تقديم تنظيمي", "سجلات رقمية"],
    benefits: ["Error-free documentation preparation", "Fast turnaround on all forms", "Digital record keeping for easy access", "Compliance with GCC and international standards"],
    benefitsAr: ["إعداد مستندات خالٍ من الأخطاء", "سرعة في إنجاز جميع النماذج", "حفظ سجلات رقمية للوصول السهل", "الامتثال للمعايير الخليجية والدولية"],
  },
};

const serviceRoutes = [
  "road-transportation", "re-export", "customs-clearance", "cross-border", "import-export-documentation"
];

export default function ServiceDetail() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const path = window.location.pathname.replace("/", "");
  const service = serviceData[path];
  const index = serviceRoutes.indexOf(path);

  if (!service) return null;

  return (
    <div className={`min-h-screen bg-white ${lang === "ar" ? "text-right" : ""}`}>
      <Helmet>
        <title>{lang === "en" ? service.metaTitle : service.metaTitleAr}</title>
        <meta name="description" content={lang === "en" ? service.metaDesc : service.metaDescAr} />
        <link rel="canonical" href={`https://abualilogistics.com/${service.slug}`} />
        <meta property="og:url" content={`https://abualilogistics.com/${service.slug}`} />
        <meta property="og:title" content={lang === "en" ? service.metaTitle : service.metaTitleAr} />
        <meta property="og:description" content={lang === "en" ? service.metaDesc : service.metaDescAr} />
        <meta name="twitter:title" content={lang === "en" ? service.metaTitle : service.metaTitleAr} />
        <meta name="twitter:description" content={lang === "en" ? service.metaDesc : service.metaDescAr} />
      </Helmet>

      {/* Spacer for fixed nav */}
      <div className="h-20"></div>

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-800 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1">
              <button onClick={() => navigate("/")} className="text-white/60 hover:text-white text-sm mb-6 flex items-center transition-colors">
                <ArrowRight className={`w-4 h-4 ${lang === "ar" ? "ml-2 rotate-180" : "mr-2"}`} />
                {t("Back to Home", "العودة إلى الرئيسية")}
              </button>
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6">
                <div className="text-white">{service.icon}</div>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
                {lang === "en" ? service.title : service.titleAr}
              </h1>
              <p className="text-xl text-blue-200 mb-6">{lang === "en" ? service.desc : service.descAr}</p>
              <p className="text-white/80 leading-relaxed max-w-2xl">{lang === "en" ? service.longDesc : service.longDescAr}</p>
            </div>
            <div className="flex-shrink-0">
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/10 w-80">
                <h3 className="text-xl font-bold text-white mb-4">{t("Quick Actions", "إجراءات سريعة")}</h3>
                <div className="space-y-3">
                  <a href={`/#contact`} className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {t("Request Quote", "طلب عرض سعر")}
                  </a>
                  <a href="https://wa.me/97334449381" target="_blank" rel="noopener noreferrer" className="w-full border-2 border-green-400/50 text-green-300 px-6 py-3 rounded-xl font-semibold hover:bg-green-500/10 transition-all duration-300 flex items-center justify-center">
                    {t("WhatsApp Us", "تواصل واتساب")}
                  </a>
                  <a href="tel:+97334449381" className="w-full border-2 border-white/30 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300 flex items-center justify-center">
                    <Phone className="w-5 h-5 mr-2" />
                    {t("Call Now", "اتصل الآن")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            {t("Key Features", "الميزات الرئيسية")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(lang === "en" ? service.features : service.featuresAr).map((f, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">{f}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            {t("Why Choose This Service", "لماذا تختار هذه الخدمة")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {(lang === "en" ? service.benefits : service.benefitsAr).map((b, i) => (
              <div key={i} className="flex items-start bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0" />
                <p className="text-gray-700">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Calendar className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t("Ready to Get Started?", "مستعد للبدء؟")}
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            {t(
              "Contact us today for a free quote and discover how our GCC logistics services can support your business.",
              "اتصل بنا اليوم للحصول على عرض سعر مجاني واكتشف كيف يمكن لخدماتنا اللوجستية في دول الخليج أن تدعم أعمالك."
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/#contact" className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              {t("Request Free Quote", "طلب عرض سعر مجاني")}
            </a>
            <a href="/" className="border-2 border-white/30 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white/10 transition-all duration-300">
              {t("View All Services", "عرض جميع الخدمات")}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
